import React, {useState, useRef, useEffect} from 'react'
import PropTypes from 'prop-types'
import styled from '@emotion/styled'
import {throttle} from 'throttle-debounce'

import SmartLink from '../../SmartLink'
import {NoInteractiveButton, QueryedButton} from '../../Button'
import Logo from '../components/Logo'

const phoneBreakpoint = '@media only screen and (max-width: 1275px)'

const height = '4.666rem'

const TopBarStyled = styled.div`
	z-index: 100;
	display: flex;
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	align-items: center;
	padding: 0 2.5rem;
	background: ${props => props.dark ? props.theme.colors.secondary : 'white'};
	overflow: hidden;
	box-sizing: border-box;
	
	transition: 250ms ease-out;
	
	${phoneBreakpoint} {
		height: ${props => `calc(${height} + ${props.heightToAdd})`};
		flex-direction: column;
		align-items: flex-start;
		padding: 0 1rem;
	}
`

const LogoLink = styled(SmartLink)`
	svg {
		display: block;
		margin: 0;
		height: 1.875rem;
	
		${phoneBreakpoint} {
			max-width: 60vw;
		}
	}
`

const ItemLink = styled(SmartLink)`
	font-weight: ${props => props.theme.fontWeights.medium};
	color: ${props => props.color};
	font-size: ${props => props.theme.fontSizes.smallest};
	margin: 0 1.5rem;
	position: relative;
	
	&:hover {
		text-decoration: none;
	}
	
	&::after {
		position: absolute;
		content: '';
		background: ${props => props.color};
		height: 1px;
		left: 0;
		right: 0;
		bottom: -.25rem;
		transform: scaleX(0);
		transform-origin: 0 0;
		transition: ${props => props.theme.transitions.smallElements} ease;
	}
	
	&:hover::after {
		transform: scaleX(1);
	}
	
	${phoneBreakpoint} {
		margin .40rem 0;
		font-size: ${props => props.theme.fontSizes.small};
		
		&::after {
			display: none;
		}
	}
	
	&.active {
		color: ${props => props.theme.colors.main};
		
		&::after {
			display: none;
		}
	}
`

const ItemsContainer = styled.div`
	margin-left: 9.8125rem;
	display: flex;
	flex: 1;
	
	${phoneBreakpoint} {
		flex-direction: column;
		margin-left: 0;
	}
`

const TopContainer = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	height: ${height};
	padding: 1.6875rem 0;
	
	${phoneBreakpoint} {
		width: 100%;
	}

`

const RestContainer = styled.div`
	display: flex;
	align-items: center;
	flex: 1;
	
	${phoneBreakpoint} {
		flex-direction: column;
		width: 100%;
		text-align: center;
		position: absolute;
		top: ${height};
		left: 0;
		right: 0;
		padding: 0 0 1.6875rem 0;
	}
`

const Buttons = styled.div`
	display: flex;
	margin-top: -0.25rem -.5rem -.5rem;
	
	> * {
		margin: .5rem;
	}
	
	${phoneBreakpoint}{
		flex-direction: column;
		align-items: center;
	}
`

const HamburgerStyled = styled(NoInteractiveButton)`
	display: none;
	margin-left: 1rem;
	
	${phoneBreakpoint} {
		display: block;
	}
`
HamburgerStyled.defaultProps = {
	noMinWidth: true,
}

function TopBar({logoLink, items, button, logoAlt, dark, loginButton}) {
	const [open, setOpen] = useState(false)
	const [forceDark, setForceDark] = useState(false)

	const restRef = useRef(null)
	const topContainerRef = useRef(null)
	const outerRef = useRef(null)

	const onScroll = throttle(500, () => {
		if (document.documentElement.scrollTop <= 0) {
			setForceDark(dark)
			return
		}
		const elementAtPoint = document.elementFromPoint(document.body.clientWidth / 2, outerRef.current.clientHeight + 1)
		if (elementAtPoint.classList.contains('darkBack') || elementAtPoint.closest('.darkBack')) setForceDark(true)
		else setForceDark(false)

	})

	useEffect(() => {
		document.addEventListener('scroll', onScroll)
		// Run this first so we can react if user reloaded scrolled page
		onScroll()

		return () => document.removeEventListener('scroll', onScroll)
	}, [])

	return <TopBarStyled
		ref={outerRef}
		id='navbar'
		heightToAdd={!open ? '0px' : `${restRef.current.clientHeight}px`}
		dark={forceDark}>
		<TopContainer ref={topContainerRef}>
			<LogoLink to={logoLink}>
				<Logo fill={!(forceDark) ? '#231F20' : 'white'}/>
			</LogoLink>
			<HamburgerStyled
				onClick={() => setOpen(current => !current)}
				open={open}>{open ? 'Close' : 'Menu'}</HamburgerStyled>
		</TopContainer>
		<RestContainer ref={restRef}>
			<ItemsContainer>
				{items.map(one => <ItemLink
					key={`${one.label}${one.link}`}
					activeClassName='active'
					color={forceDark ? 'white' : 'black'}
					to={one.link}>{one.label}</ItemLink>)}
			</ItemsContainer>
			<Buttons>
				<QueryedButton data={button}/>
				<QueryedButton inverted noMinWidth data={loginButton}/>
			</Buttons>
		</RestContainer>
	</TopBarStyled>
}

export default TopBar

TopBar.propTypes = {
	logoLink: PropTypes.string.isRequired,
	dark: PropTypes.bool,
	logoAlt: PropTypes.string.isRequired,
	items: PropTypes.arrayOf(PropTypes.shape({
		link: PropTypes.string.isRequired,
		label: PropTypes.string.isRequired,
	})).isRequired,
	button: PropTypes.shape({
		link: PropTypes.string.isRequired,
		label: PropTypes.string.isRequired,
	}).isRequired,
	loginButton: PropTypes.shape({
		link: PropTypes.string.isRequired,
		label: PropTypes.string.isRequired,
	}).isRequired,
}

TopBar.defaultProps = {
	dark: false,
}
